import React, {useEffect, useState, useRef} from 'react';
import {useAuth} from '../../contexts/AuthContext';
import {getTwoFactorSetup, verifyTwoFactorCode} from '../../../components/requests';
import {toast} from 'react-toastify';
import {useLocation, useNavigate} from 'react-router-dom';
import SwitchMethod from "./components/SwitchMethod";

type SetupStep = 'qr' | 'verify' | 'backup';

// Type pour les données utilisateurs passés en state dans navigate pour les modifications
interface LocationState {
  id: any
  twoFactorMethod: any
}

const TwoFactorSetupPage: React.FC = () => {
  const {completeTwoFactor, logout} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const [step, setStep] = useState<SetupStep>('qr');
  const [secret, setSecret] = useState('');
  const [qrCodeDataUri, setQrCodeDataUri] = useState('');
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const PUBLIC_URL = process.env.PUBLIC_URL;

  useEffect(() => {
    getTwoFactorSetup()
      .then((response) => {
        if (response.status === 200) {
          setSecret(response.data.secret);
          setQrCodeDataUri(response.data.qrCodeDataUri);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Erreur lors de la configuration du 2FA');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);
    if (pastedData.length > 0) {
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Veuillez saisir un code à 6 chiffres');
      return;
    }

    setError('');
    setIsVerifying(true);

    try {
      const response = await verifyTwoFactorCode(fullCode);
      if (response.status === 200) {
        const data = response.data;
        if (data.backupCodes) {
          setBackupCodes(data.backupCodes);
          localStorage.setItem('accessToken', data.token);
          setStep('backup');
        } else {
          await completeTwoFactor(data.token);
          navigate('/accueil');
          toast.success('Authentification à deux facteurs activée');
        }
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Code invalide. Veuillez réessayer.');
      } else {
        setError('Une erreur est survenue');
      }
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCopyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFinish = async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      await completeTwoFactor(token);
      navigate('/accueil');
      toast.success('Authentification à deux facteurs activée');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)'}}>
        <svg className="animate-spin w-10 h-10 text-emerald-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
        </svg>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{background: 'linear-gradient(135deg, #0f172a 0%, #1e3a2f 50%, #14532d 100%)'}}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-emerald-500 rounded-full blur-3xl"/>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl"/>
        </div>

        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-2xl font-bold">
                <img src={`${PUBLIC_URL}/favicon.ico`} alt="logo"/>
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">GROUPE SURPLUS</h1>
              <p className="text-emerald-400 text-sm tracking-widest font-medium">RECYCLAGE</p>
            </div>
          </div>

          <h2 className="text-5xl font-bold mb-6 leading-tight">
            Sécurisation
            <br/>
            <span className="text-emerald-400">du compte</span>
          </h2>

          <p className="text-gray-300 text-lg max-w-md mb-10 leading-relaxed">
            Configurez l'authentification à deux facteurs pour protéger votre compte.
          </p>
        </div>
      </div>

      {/* Right Panel - Content */}
      <div
        className="flex-1 flex items-center justify-center p-8"
        style={{background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)'}}
      >
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center">
              <span className="text-lg font-bold text-white">
                <img src={`${PUBLIC_URL}/favicon.ico`} alt="logo"/>
              </span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">GROUPE SURPLUS</h1>
              <p className="text-emerald-600 text-xs tracking-widest">RECYCLAGE</p>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-10 shadow-xl border border-gray-100">
            {/* Step indicators */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {['qr', 'verify', 'backup'].map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === s ? 'bg-emerald-500 text-white' :
                    ['qr', 'verify', 'backup'].indexOf(step) > i ? 'bg-emerald-100 text-emerald-700' :
                    'bg-gray-100 text-gray-400'
                  }`}>
                    {i + 1}
                  </div>
                  {i < 2 && <div className={`w-8 h-0.5 ${['qr', 'verify', 'backup'].indexOf(step) > i ? 'bg-emerald-300' : 'bg-gray-200'}`}/>}
                </div>
              ))}
            </div>

            {/* Step 1: QR Code */}
            {step === 'qr' && (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Configurer le 2FA</h3>
                <p className="text-gray-500 mb-6 text-sm">
                  Scannez ce QR code avec votre application d'authentification (Google Authenticator, Authy...).
                </p>

                <div className="flex justify-center mb-6">
                  <img src={qrCodeDataUri} alt="QR Code 2FA" className="rounded-lg border border-gray-200"/>
                </div>

                <div className="mb-6">
                  <p className="text-xs text-gray-500 mb-2">Ou saisissez cette clé manuellement :</p>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-sm text-center break-all select-all">
                    {secret}
                  </div>
                </div>

                <button
                  onClick={() => {
                    setStep('verify');
                    setTimeout(() => inputRefs.current[0]?.focus(), 100);
                  }}
                  className="w-full py-3.5 rounded-xl text-white font-semibold bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-lg shadow-emerald-500/25"
                >
                  Continuer
                </button>
              </>
            )}

            {/* Step 2: Verify Code */}
            {step === 'verify' && (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Vérification</h3>
                <p className="text-gray-500 mb-6 text-sm">
                  Saisissez le code à 6 chiffres affiché dans votre application d'authentification.
                </p>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                    </svg>
                    {error}
                  </div>
                )}

                <form>
                <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { inputRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-12 h-14 text-center text-xl font-bold rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all"
                    />
                  ))}
                </div>

                <button
                  type="submit"
                  onClick={handleVerify}
                  disabled={isVerifying || code.join('').length !== 6}
                  className="w-full py-3.5 rounded-xl text-white font-semibold bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isVerifying ? (
                    <>
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Vérification...
                    </>
                  ) : 'Vérifier'}
                </button>
                </form>

                <button
                  onClick={() => setStep('qr')}
                  className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-gray-700"
                >
                  Retour au QR code
                </button>
              </>
            )}

            {/* Step 3: Backup Codes */}
            {step === 'backup' && (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Codes de secours</h3>
                <p className="text-gray-500 mb-4 text-sm">
                  Conservez ces codes dans un endroit sûr. Ils vous permettront de vous connecter si vous n'avez plus accès à votre application d'authentification.
                </p>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                  <p className="text-amber-800 text-xs font-medium flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                    </svg>
                    Ces codes ne seront plus affichés. Notez-les maintenant !
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-6">
                  {backupCodes.map((bcode, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-2 font-mono text-sm text-center select-all">
                      {bcode}
                    </div>
                  ))}
                </div>

                <button
                  onClick={handleCopyBackupCodes}
                  className="w-full py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-all mb-3 flex items-center justify-center gap-2"
                >
                  {copied ? (
                    <>
                      <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                      </svg>
                      Copié !
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"/>
                      </svg>
                      Copier les codes
                    </>
                  )}
                </button>

                <button
                  onClick={handleFinish}
                  className="w-full py-3.5 rounded-xl text-white font-semibold bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-lg shadow-emerald-500/25"
                >
                  J'ai noté mes codes, continuer
                </button>
              </>
            )}

            <p className="mt-6 text-center text-sm text-gray-500">
              <button onClick={logout} className="text-emerald-600 hover:text-emerald-700 font-medium">
                Se déconnecter
              </button>
            </p>

            {/* Sélecteur de méthode */}
            <SwitchMethod
              id={locationState?.id}
              method={locationState?.twoFactorMethod}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetupPage;
